import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import { formatTurkeyLocalDateTime } from '../../../../utils/turkeyLocalDateTime';

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return formatTurkeyLocalDateTime(value);
  } catch {
    return new Date(value).toLocaleString('tr-TR');
  }
};

const TABLE_HEAD_CELL_SX = {
  fontSize: '13px',
  fontWeight: 500,
  borderBottom: '1px dashed #e8e8e8',
  px: 1.5,
  py: 1.5,
  color: '#a3a3a3',
  whiteSpace: 'nowrap',
};

const TABLE_BODY_CELL_SX = {
  py: 1,
  fontSize: '14px',
  color: '#444444',
  borderBottom: '1px dashed #e8e8e8',
};

const chipSx = (bg, color) => ({
  height: 24,
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '12px',
  border: 'none',
  backgroundColor: bg,
  color,
});

const normalizeRow = (row) => ({
  section: pick(row, 'section', 'bolum', 'Bolum', 'Section'),
  type: pick(row, 'typeName', 'type', 'tip', 'Tip', 'TypeName'),
  fullName: pick(row, 'fullName', 'adSoyad', 'AdSoyad', 'FullName'),
  status: pick(row, 'status', 'statusName', 'durum', 'Durum', 'StatusName'),
  attended: pick(row, 'attended', 'katildi', 'Katildi'),
  absent: pick(row, 'absent', 'gelmedi', 'Gelmedi'),
  phoneNumber: pick(row, 'phoneNumber', 'phone', 'telefon', 'Telefon', 'PhoneNumber', 'Phone'),
  parentFullName: pick(row, 'parentFullName', 'ustUyeAdi', 'UstUyeAdi', 'ParentFullName'),
  isApproved: pick(row, 'isApproved', 'onaylandi', 'Onaylandi', 'IsApproved'),
  registeredAt: pick(row, 'registeredAt', 'kayitTarihi', 'KayitTarihi', 'RegisteredAt'),
  attendedAt: pick(row, 'attendedAt', 'katilimZamani', 'KatilimZamani', 'AttendedAt'),
  queueNumber: pick(row, 'queueNumber', 'siraNo', 'SiraNo', 'QueueNumber'),
  guestCount: pick(row, 'guestCount', 'misafirSayisi', 'MisafirSayisi', 'GuestCount'),
  hostFullName: pick(row, 'hostFullName', 'evSahibiAdi', 'EvSahibiAdi', 'HostFullName'),
});

const resolveSummary = (data) => {
  const nested = data.summary || data.Summary;
  if (nested && typeof nested === 'object' && Object.keys(nested).length > 0) {
    return nested;
  }

  return {
    totalParticipants: pick(data, 'totalParticipants', 'TotalParticipants'),
    approvedCount: pick(data, 'approvedCount', 'ApprovedCount'),
    attendedCount: pick(data, 'attendedCount', 'AttendedCount'),
    absentCount: pick(data, 'absentCount', 'AbsentCount'),
    cancelledCount: pick(data, 'cancelledCount', 'CancelledCount'),
    pendingCount: pick(data, 'pendingCount', 'PendingCount'),
    waitingListCount: pick(data, 'waitingListCount', 'WaitingListCount'),
    totalGuests: pick(data, 'totalGuests', 'TotalGuests'),
    guestAttendedCount: pick(data, 'guestAttendedCount', 'GuestAttendedCount'),
    guestAbsentCount: pick(data, 'guestAbsentCount', 'GuestAbsentCount'),
    guestCancelledCount: pick(data, 'guestCancelledCount', 'GuestCancelledCount'),
    guestPendingCount: pick(data, 'guestPendingCount', 'GuestPendingCount'),
    attendanceRate: pick(data, 'attendanceRate', 'AttendanceRate'),
    cancellationRate: pick(data, 'cancellationRate', 'CancellationRate'),
    absenceRate: pick(data, 'absenceRate', 'AbsenceRate'),
  };
};

const normalizeParticipant = (p) => ({
  id: pick(p, 'participantId', 'id', 'ParticipantId', 'personId', 'PersonId'),
  fullName: pick(p, 'fullName', 'FullName'),
  parentFullName: pick(p, 'parentFullName', 'ParentFullName', 'ustUyeAdi', 'UstUyeAdi'),
  typeName: pick(p, 'typeName', 'TypeName', 'type', 'Tip'),
  statusName: pick(p, 'statusName', 'StatusName', 'durum', 'Durum'),
  isApproved: pick(p, 'isApproved', 'IsApproved', 'onaylandi', 'Onaylandi'),
  registeredAt: pick(p, 'registeredAt', 'RegisteredAt', 'kayitTarihi', 'KayitTarihi'),
  attendedAt: pick(p, 'attendedAt', 'AttendedAt', 'katilimZamani', 'KatilimZamani'),
  queueNumber: pick(p, 'queueNumber', 'QueueNumber', 'siraNo', 'SiraNo'),
  guestCount: pick(p, 'guestCount', 'GuestCount', 'misafirSayisi', 'MisafirSayisi'),
  phoneNumber: pick(p, 'phoneNumber', 'PhoneNumber', 'telefon', 'Telefon'),
});

const normalizeGuest = (g) => ({
  id: pick(g, 'guestId', 'id', 'GuestId'),
  fullName: pick(g, 'fullName', 'FullName', 'adSoyad', 'AdSoyad') || [g.firstName, g.lastName].filter(Boolean).join(' '),
  hostFullName: pick(g, 'hostFullName', 'HostFullName', 'evSahibiAdi', 'EvSahibiAdi'),
  phoneNumber: pick(g, 'phoneNumber', 'PhoneNumber', 'telefon', 'Telefon'),
  statusName: pick(g, 'statusName', 'StatusName', 'durum', 'Durum'),
  registeredAt: pick(g, 'registeredAt', 'RegisteredAt', 'kayitTarihi', 'KayitTarihi'),
  attendedAt: pick(g, 'attendedAt', 'AttendedAt', 'katilimZamani', 'KatilimZamani'),
  queueNumber: pick(g, 'queueNumber', 'QueueNumber', 'siraNo', 'SiraNo'),
});

const formatYesNo = (value) => {
  if (value === true || value === 'Evet' || value === 'evet') return 'Evet';
  if (value === false || value === 'Hayır' || value === 'hayir' || value === 'Hayir') return 'Hayır';
  return value ?? '-';
};

const ReportTable = ({ title, icon: Icon, columns, rows, emptyMessage = 'Kayıt yok' }) => (
  <Box>
    <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1.5 }}>
      {Icon && <Icon sx={{ fontSize: 20, color: '#0E4D94' }} />}
      <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
        {title} ({rows.length})
      </Typography>
    </Stack>
    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none', borderRadius: 0, overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 720 }}>
        <TableHead sx={{ bgcolor: 'transparent' }}>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} sx={TABLE_HEAD_CELL_SX}>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 3, fontSize: '14px', color: '#a3a3a3', borderBottom: 'none' }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, idx) => (
              <TableRow
                key={row.id || `${title}-${idx}`}
                sx={{
                  '& .MuiTableCell-root': {
                    ...TABLE_BODY_CELL_SX,
                    borderBottom: idx === rows.length - 1 ? 'none' : TABLE_BODY_CELL_SX.borderBottom,
                  },
                }}
              >
                {columns.map((col) => (
                  <TableCell key={col.key}>{col.render(row)}</TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

const EventAttendanceReportDialog = ({ open, onClose, data }) => {
  const report = useMemo(() => {
    if (!data) return null;

    const summary = resolveSummary(data);
    const rows = Array.isArray(data.rows) ? data.rows
      : Array.isArray(data.Rows) ? data.Rows
        : Array.isArray(data.items) ? data.items
          : Array.isArray(data.Items) ? data.Items
            : null;

    const participants = (Array.isArray(data.participants) ? data.participants : Array.isArray(data.Participants) ? data.Participants : [])
      .map(normalizeParticipant);
    const guests = (Array.isArray(data.guests) ? data.guests : Array.isArray(data.Guests) ? data.Guests : [])
      .map(normalizeGuest);
    const waitingList = (Array.isArray(data.waitingList) ? data.waitingList : Array.isArray(data.WaitingList) ? data.WaitingList : [])
      .map(normalizeParticipant);

    return {
      title: pick(data, 'eventTitle', 'title', 'EventTitle', 'Title') || '-',
      startDate: pick(data, 'startDate', 'StartDate'),
      endDate: pick(data, 'endDate', 'EndDate'),
      city: pick(data, 'city', 'City'),
      venue: pick(data, 'venueName', 'venue', 'location', 'VenueName', 'Venue', 'Location'),
      capacity: pick(data, 'capacity', 'Capacity'),
      summary,
      rows: rows ? rows.map(normalizeRow) : null,
      participants,
      guests,
      waitingList,
      updatedAt: pick(data, 'updatedAt', 'UpdatedAt', 'generatedAt', 'GeneratedAt'),
    };
  }, [data]);

  if (!open) return null;

  const summary = report?.summary || {};
  const summaryChips = [
    { label: `Kayıtlı: ${pick(summary, 'totalParticipants', 'registeredCount', 'TotalParticipants', 'RegisteredCount') ?? 0}`, bg: '#f5f5f5', color: '#444444' },
    { label: `Onaylı: ${pick(summary, 'approvedCount', 'ApprovedCount') ?? 0}`, bg: '#DFFFEA', color: '#17C653' },
    { label: `Katıldı: ${pick(summary, 'attendedCount', 'AttendedCount') ?? 0}`, bg: '#e8f5e9', color: '#2e7d32' },
    { label: `Gelmedi: ${pick(summary, 'absentCount', 'AbsentCount') ?? 0}`, bg: '#ffebee', color: '#c62828' },
    { label: `İptal: ${pick(summary, 'cancelledCount', 'CancelledCount') ?? 0}`, bg: '#fff3e0', color: '#e65100' },
    { label: `Beklemede: ${pick(summary, 'pendingCount', 'PendingCount') ?? 0}`, bg: '#f5f5f5', color: '#444444' },
    { label: `Sırada: ${pick(summary, 'waitingListCount', 'WaitingListCount') ?? 0}`, bg: '#E7EDF4', color: '#0E4D94' },
    { label: `Misafir: ${pick(summary, 'totalGuests', 'TotalGuests') ?? 0}`, bg: '#e8eaf6', color: '#3949ab' },
    { label: `Katılım: %${Number(pick(summary, 'attendanceRate', 'AttendanceRate') ?? 0).toFixed(0)}`, bg: '#f5f5f5', color: '#444444' },
    { label: `İptal Oranı: %${Number(pick(summary, 'cancellationRate', 'CancellationRate') ?? 0).toFixed(0)}`, bg: '#fff3e0', color: '#e65100' },
  ];

  const unifiedColumns = [
    { key: 'section', label: 'Bölüm', render: (row) => row.section || '-' },
    { key: 'type', label: 'Tip', render: (row) => row.type || '-' },
    { key: 'fullName', label: 'Ad Soyad', render: (row) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.fullName || '-'}</Typography>
        {row.parentFullName && (
          <Typography variant="caption" sx={{ fontSize: '12px', color: '#a3a3a3', display: 'block' }}>
            Üye: {row.parentFullName}
          </Typography>
        )}
      </Box>
    ) },
    { key: 'status', label: 'Durum', render: (row) => row.status || '-' },
    { key: 'phone', label: 'Telefon', render: (row) => row.phoneNumber || '-' },
    { key: 'approved', label: 'Onay', render: (row) => formatYesNo(row.isApproved) },
    { key: 'registeredAt', label: 'Kayıt', render: (row) => formatDateTime(row.registeredAt) },
    { key: 'attendedAt', label: 'Katılım', render: (row) => formatDateTime(row.attendedAt) },
    { key: 'queue', label: 'Sıra', render: (row) => (typeof row.queueNumber === 'number' ? `#${row.queueNumber}` : '-') },
    { key: 'host', label: 'Ev Sahibi', render: (row) => row.hostFullName || '-' },
  ];

  const participantColumns = [
    { key: 'fullName', label: 'Ad Soyad', render: (row) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.fullName || '-'}</Typography>
        {row.parentFullName && (
          <Typography variant="caption" sx={{ fontSize: '12px', color: '#a3a3a3', display: 'block' }}>
            Üye: {row.parentFullName}
          </Typography>
        )}
      </Box>
    ) },
    { key: 'type', label: 'Tür', render: (row) => row.typeName || '-' },
    { key: 'status', label: 'Durum', render: (row) => row.statusName || '-' },
    { key: 'phone', label: 'Telefon', render: (row) => row.phoneNumber || '-' },
    { key: 'approved', label: 'Onay', render: (row) => formatYesNo(row.isApproved) },
    { key: 'registeredAt', label: 'Kayıt', render: (row) => formatDateTime(row.registeredAt) },
    { key: 'attendedAt', label: 'Katılım', render: (row) => formatDateTime(row.attendedAt) },
    { key: 'guestCount', label: 'Misafir', render: (row) => ((row.guestCount ?? 0) > 0 ? row.guestCount : '-') },
  ];

  const guestColumns = [
    { key: 'fullName', label: 'Ad Soyad', render: (row) => row.fullName || '-' },
    { key: 'host', label: 'Getiren', render: (row) => row.hostFullName || '-' },
    { key: 'phone', label: 'Telefon', render: (row) => row.phoneNumber || '-' },
    { key: 'status', label: 'Durum', render: (row) => row.statusName || '-' },
    { key: 'registeredAt', label: 'Kayıt', render: (row) => formatDateTime(row.registeredAt) },
    { key: 'attendedAt', label: 'Katılım', render: (row) => formatDateTime(row.attendedAt) },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          fontSize: '18px',
          fontWeight: 500,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 3,
        }}
      >
        Katılım Raporu
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2, px: 3 }}>
        {!report ? (
          <Typography sx={{ fontSize: '14px', color: '#a3a3a3', py: 2 }}>Rapor yüklenemedi.</Typography>
        ) : (
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 1.5 }}>
                {report.title}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {report.startDate && (
                  <Chip
                    icon={<ScheduleIcon sx={{ fontSize: '16px !important' }} />}
                    label={`Başlangıç: ${formatDateTime(report.startDate)}`}
                    size="small"
                    sx={{ ...chipSx('#f5f5f5', '#444444'), '& .MuiChip-icon': { color: '#a3a3a3' } }}
                  />
                )}
                {report.endDate && (
                  <Chip
                    icon={<ScheduleIcon sx={{ fontSize: '16px !important' }} />}
                    label={`Bitiş: ${formatDateTime(report.endDate)}`}
                    size="small"
                    sx={{ ...chipSx('#f5f5f5', '#444444'), '& .MuiChip-icon': { color: '#a3a3a3' } }}
                  />
                )}
                {report.city && (
                  <Chip
                    icon={<LocationOnIcon sx={{ fontSize: '16px !important' }} />}
                    label={report.city}
                    size="small"
                    sx={{ ...chipSx('#f5f5f5', '#444444'), '& .MuiChip-icon': { color: '#a3a3a3' } }}
                  />
                )}
                {report.venue && (
                  <Chip
                    icon={<LocationOnIcon sx={{ fontSize: '16px !important' }} />}
                    label={report.venue}
                    size="small"
                    sx={{ ...chipSx('#f5f5f5', '#444444'), '& .MuiChip-icon': { color: '#a3a3a3' } }}
                  />
                )}
                {typeof report.capacity === 'number' && (
                  <Chip
                    icon={<EventSeatIcon sx={{ fontSize: '16px !important' }} />}
                    label={`Kapasite: ${report.capacity}`}
                    size="small"
                    sx={{ ...chipSx('#E7EDF4', '#0E4D94'), '& .MuiChip-icon': { color: '#0E4D94' } }}
                  />
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', fontWeight: 500, mb: 1 }}>
                Özet
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {summaryChips.map((chip) => (
                  <Chip key={chip.label} size="small" label={chip.label} sx={chipSx(chip.bg, chip.color)} />
                ))}
              </Stack>
            </Box>

            <Divider sx={{ borderColor: '#e6e6e6' }} />

            {report.rows ? (
              <ReportTable
                title="Katılım Listesi"
                icon={PeopleIcon}
                columns={unifiedColumns}
                rows={report.rows}
              />
            ) : (
              <>
                <ReportTable
                  title="Katılımcılar"
                  icon={PeopleIcon}
                  columns={participantColumns}
                  rows={report.participants}
                />
                {report.guests.length > 0 && (
                  <ReportTable
                    title="Misafirler"
                    icon={PersonIcon}
                    columns={guestColumns}
                    rows={report.guests}
                  />
                )}
                {report.waitingList.length > 0 && (
                  <ReportTable
                    title="Bekleme Listesi"
                    icon={EventSeatIcon}
                    columns={participantColumns}
                    rows={report.waitingList}
                  />
                )}
              </>
            )}

            {report.updatedAt && (
              <Typography variant="caption" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                Oluşturulma: {formatDateTime(report.updatedAt)}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            color: '#666666',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventAttendanceReportDialog;
